import { Component, computed, inject, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ServicePortalApi } from '../../../api/api';
import { SimplifiedClientInfo, Role, RoleInput, RoleMember, UserReference } from '../../../api/types';
import {
  SynAlertComponent,
  SynBreadcrumbComponent,
  SynBreadcrumbItemComponent,
  SynButtonComponent,
  SynDialogComponent,
  SynIconButtonComponent,
  SynIconComponent,
  SynInputComponent, SynSpinnerComponent,
} from '@synergy-design-system/angular';
import { RoleFormComponent } from './role-form.component';
import { globalClientId } from '../../state';
import { ColumnDescription, TableComponent } from '../../components/table';
import { UserSearchComponent } from '../../components/user-search.component';

@Component({
  selector: 'app-edit-role',
  template: `
    <syn-breadcrumb>
      <syn-breadcrumb-item routerLink="" i18n="@@home">Home</syn-breadcrumb-item>
      <syn-breadcrumb-item routerLink="/clients" i18n="@@clients">Clients</syn-breadcrumb-item>
      <syn-breadcrumb-item [routerLink]="['/', 'clients', clientId]">{{ globalClientId() ?? clientId }}
      </syn-breadcrumb-item>
      <syn-breadcrumb-item [routerLink]="['/', 'clients', clientId, 'roles']" i18n="@@roles">Roles</syn-breadcrumb-item>
      <syn-breadcrumb-item>{{ role()?.name ?? '' }}</syn-breadcrumb-item>
    </syn-breadcrumb>
    <div class="flex-row gap-2 align-center margin-bottom-1">
      <h1 i18n="@@edit-role">Edit Role</h1>
      <syn-button
        (click)="openDeleteDialog()"
        size="small"
        [disabled]="!canEdit()"
      >
        <syn-icon name="delete_fill" slot="prefix" />
        <span i18n="@@delete-role">Delete Role</span>
      </syn-button>
    </div>
    <div class="flex-row gap-2 margin-bottom-1">
      <!-- Add loading indication until data is available -->
      @if (role()) {
        <div class="flex-row gap-1 align-top">
          <app-role-form
            [canEdit]="canEdit()"
            [role]="role()!"
            submitIcon="save"
            submitText="Save Changes"
            i18n-submitText="@@save-changes"
            (submitRole)="onSubmit($event)"
          />
        </div>
      }
      @if (mappedByClientIdList().length > 0) {
        <div class="flex-col align-top">
          <h3><span i18n="@@role-clientlist">Clients that use this role</span></h3>
          <p i18n="@@role-clientlist-info">
            This role is mapped into the tokens of the following clients. <br />If you delete this role, these clients
            might
            not work as expected.
          </p>
          <ul>
            @for (entry of mappedByClientIdList(); track entry.clientId) {
              <li>
                {{ entry.clientId }}
              </li>
            }
          </ul>
        </div>
      }
    </div>

    <div class="flex-row gap-2 align-center">
      <h2>
        <span i18n="@@members">Members</span>
        {{ memberCountDisplay() }}
      </h2>
      @if (hasMoreMembers()) {
        <syn-button
          (click)="loadAllMembers()"
          [disabled]="loading()"
        >
          @if (loading()) {
            <syn-spinner slot="prefix" />
          }
          <span i18n="@@load-all-members">Load All Members</span>
        </syn-button>
      }
    </div>

    <div class="margin-bottom-1">
      <app-user-search
        [disabled]="loading()"
        actionText="Add User"
        i18n-actionText="@@add-user"
        (userSelected)="onAddMember($event)"
      />
    </div>

    @if (role()?.name === 'client_admin') {
      <syn-alert open variant="warning">
        <span i18n="@@client-admin-role-warning">
          This role is only used to define the client administrators in the old service portal.
          For the new serviceportal, the permissions are managed in the permissions tab.
        </span>
        <syn-icon slot="icon" name="warning_fill" />
      </syn-alert>
    }
    <ng-template #rowTemplate let-value>
      <!-- Enable type checking in the template -->
      @let member = identity(value);
      <tr>
        <td>{{ member.username }}</td>
        <td>{{ member.email }}</td>
        <td>{{ member.firstName }}</td>
        <td>{{ member.lastName }}</td>
        <td>
          <syn-icon-button
            name="delete_fill"
            label="Remove Member"
            i18n-label="@@remove-member"
            size="medium"
            [disabled]="removingMember()"
            (click)="onRemoveMember(member)"
          />
        </td>
      </tr>
    </ng-template>
    <app-table
      [columns]="columns"
      [values]="members()"
      [rowTemplate]="rowTemplate"
    />

    <syn-dialog
      label="Delete Role"
      i18n-label="@@delete-role"
      #deleteDialog>
      <div>
        <p i18n="@@delete-role-warning">
          Do you want to <strong>permanently delete</strong> the following client role?
        </p>
        <span>{{ this.role()?.name }}</span>
        @if (mappedByClientIdList().length > 0) {
          <p i18n="@@delete-role-warning-clientlist">
            This role is mapped into the tokens of the following clients:
          </p>
          <ul>
            @for (entry of mappedByClientIdList(); track entry.clientId) {
              <li>
                {{ entry.clientId }}
              </li>
            }
          </ul>
          <p i18n="@@delete-role-warning-clientlist-info">
            These clients might rely on the role for their functionality.
          </p>
        }
        <p i18n="@@delete-role-confirmation">
          Please enter the role name to confirm deletion.
        </p>
        <syn-input [(value)]="roleNameToDelete" />
      </div>
      <div slot="footer" class="flex-row gap-1 justify-end">
        <syn-button i18n="@@cancel" (click)="closeDeleteDialog()">Cancel</syn-button>
        <syn-button variant="filled" (click)="onDeleteRole()" [disabled]="!canDelete()">
          <span i18n="@@delete-role">Delete Role</span>
        </syn-button>
      </div>
    </syn-dialog>
  `,
  styles: [],
  imports: [
    SynButtonComponent,
    SynIconComponent,
    SynIconButtonComponent,
    SynDialogComponent,
    RoleFormComponent,
    RouterLink,
    SynBreadcrumbComponent,
    SynBreadcrumbItemComponent,
    TableComponent,
    SynAlertComponent,
    UserSearchComponent,
    SynInputComponent,
    SynSpinnerComponent,
  ],
})
export class EditRoleComponent implements OnInit {
  clientId = inject(ActivatedRoute).snapshot.params['id'];
  roleId = inject(ActivatedRoute).snapshot.params['roleId'];
  api = inject(ServicePortalApi);
  router = inject(Router);

  role: WritableSignal<Role | null> = signal(null);
  members: WritableSignal<RoleMember[]> = signal([]);
  memberPageSize = signal(1000);
  hasMoreMembers = signal(false);
  mappedByClientIdList: WritableSignal<SimplifiedClientInfo[]> = signal([]);
  removingMember = signal(false);
  loading = signal(false);

  canEdit = signal(true);
  roleNameToDelete = signal('');
  canDelete = computed(() => this.roleNameToDelete() === this.role()?.name);

  memberCountDisplay = computed(() => {
    const length = this.members().length;
    if (length === 0) {
      return '';
    }
    const more = this.hasMoreMembers() ? '+' : '';
    return '(' + length + more + ')';
  });

  @ViewChild(SynDialogComponent) deleteDialog!: SynDialogComponent;
  @ViewChild(UserSearchComponent) addMemberInput!: UserSearchComponent;

  columns: ColumnDescription<RoleMember>[] = [
    {
      label: $localize`:@@username:Username`,
      getField: (member) => member.username,
    },
    {
      label: $localize`:@@email:Email`,
      getField: (member) => member.email ?? '',
    },
    {
      label: $localize`:@@first-name:First Name`,
      getField: (member) => member.firstName ?? '',
    },
    {
      label: $localize`:@@last-name:Last Name`,
      getField: (member) => member.lastName ?? '',
    },
    {
      label: $localize`:@@remove:Remove`,
      getField: () => '',
    },
  ];

  ngOnInit() {
    this.loading.set(true);

    this.api.getClientRoleDetails(this.clientId, this.roleId, 0, this.memberPageSize(),
      (value) => {
        this.role.set(value.role);
        switch (value.role.associatedPermissionRole) {
          case 'client_admin':
          case 'role_admin':
            this.canEdit.set(true);
            break;

          default:
            this.canEdit.set(false);
            break;
        }
        this.members.set(value.members.members);
        this.hasMoreMembers.set(value.members.members.length === this.memberPageSize());
        value.mappedByClientList = value.mappedByClientList.filter(
          (entry) => entry.id !== this.clientId,
        );
        this.mappedByClientIdList.set(value.mappedByClientList);
        this.loading.set(false);
      });
  }

  loadAllMembers() {
    this.loading.set(true);
    const first = this.members().length;
    this.api.getClientRoleMembers(this.clientId, this.roleId, first, this.memberPageSize(),
      (value) => {
        console.log('Loaded next page with ' + value.members.length + ' members');

        this.members.update((members) => members.concat(value.members));

        const more = value.members.length === this.memberPageSize();
        this.hasMoreMembers.set(more);
        if (more) {
          this.loadAllMembers();
        } else {
          console.log('Finished loading members: ', this.members().length);
          this.loading.set(false);
        }
      });
  }

  onAddMember(userToAdd: UserReference) {
    this.loading.set(true);
    this.api.createClientRoleMember(this.clientId, this.roleId, userToAdd,
      (newMember) => {
        this.loading.set(false);
        this.addMemberInput.clear();

        const alreadyMember = this.members().some(
          (member) => member.id === newMember.id);
        if (alreadyMember) return;

        const newMembers = [newMember, ...this.members()];
        this.members.set(newMembers);
      },
      () => {
        this.addMemberInput.setUserDoesNotExistError();
        this.loading.set(false);
      });
  }

  onRemoveMember(memberToRemove: RoleMember) {
    this.removingMember.set(true);
    this.loading.set(true);
    this.api.deleteClientRoleMember(this.clientId, this.roleId, memberToRemove.id, () => {
      this.removingMember.set(false);
      this.loading.set(false);
      this.members.update((members) => {
        return members.filter((member) => member !== memberToRemove);
      });
    });
  }

  openDeleteDialog() {
    void this.deleteDialog.nativeElement?.show();

  }

  closeDeleteDialog() {
    void this.deleteDialog.nativeElement?.hide();
  }

  onDeleteRole() {
    this.api.deleteClientRole(this.clientId, this.roleId, () => {
      console.log('Client role deleted', this.roleId);
      void this.router.navigate(['clients', this.clientId, 'roles']);
    });
  }

  onSubmit(role: RoleInput) {
    this.api.updateClientRole(this.clientId, this.roleId, role, (updatedRole) => {
      this.role.set(updatedRole);
    });
  }

  identity(member: RoleMember) {
    return member;
  }

  protected readonly globalClientId = globalClientId;
}
